import type { Models as FlightModels } from '@luxuryescapes/lib-flights-types'
import type { definitions as flightDefinitions, paths } from '@luxuryescapes/contract-svc-flights'
import { templates } from '@luxuryescapes/lib-uri-templates'
import request, { authOptions } from 'api/requestUtils'
import type {
  Airline,
  FlightCachedPrice,
  FlightCalendarPrices,
  FlightsCreditDetails,
} from 'components/Flights/types'
import type { CreateFareWatchRequest, CreateFareWatchResponse, FareWatch, GetFareWatchesResponse, FareWatchStatusQuery, FareWatchStatusResponse, UpdateFareWatchRequest, UpdateFareWatchResponse } from 'components/Flights/PriceAlerts/types'
import config from 'constants/config'
import type { CharterPromotionType } from 'constants/charterFlight'
import { FlightsFareTypes } from 'constants/flight'
import { nonNullable, sortBy } from 'lib/array/arrayUtils'
import {
  DOMESTIC_COMBINED_JOURNEY_COMPATIBILITY_GROUPS,
  getTwoOneWayCompatibilityGroup,
  MAX_DOMESTIC_COMBINED_JOURNEYS,
  selectDomesticCombinedJourneyShortlist,
  sortDomesticCombinedFlightJourneys,
} from 'lib/flights/domesticFlightRanking'
import { EmptyObject } from 'lib/object/objectUtils'
import { getFlightSearchIsLuxPlusParam } from 'lib/flights/flightLuxPlusPricing'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import qs from 'qs'
import { flightDealMap } from './lib/flightDealMap'
import type { ServerUpsellFareFamily } from './lib/flightFareFamilyMap'
import { mapFlightFareFamily } from './lib/flightFareFamilyMap'
import { journeyV2Map } from './lib/journeyV2Map'
import { seatsAvailabilityMap } from './lib/seatsAvailabilityMap'
import { baggageAvailabilityMap } from './lib/baggageAvailabilityMap'
import { postBookingBaggageMap } from './lib/postBookingBaggageMap'
import type { PostBookingBaggageAvailability } from './lib/postBookingBaggageMap'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import type { TrackFlightSearchCompletedParams } from 'analytics/snowplow/events/flightSearchCompletedEvent'
import { trackFlightSearchCompletedEvent } from 'analytics/snowplow/events/flightSearchCompletedEvent'
import { resetFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import type { Fare, OneWayFare } from './flights.types'
import { getAirportsSearch } from './flightsAirportSearch'

export type { Fare, FlightSearchPrice } from './flights.types'
export { getAirportsSearch }

export interface FlightSearchItinerary {
  arrivalDate?: string
  arrivalWindow?: string
  departureWindow?: string
  departureAirport: string
  arrivalAirport: string
  departureDate: string
}

export type ServerSeatResponse = flightDefinitions['SeatsAvailabilityResponse']

interface SearchParams {
  itinerary: Array<FlightSearchItinerary>
  currency: string
  occupancies: Array<App.Occupants>
  fareClass: string
  maxArrivalTime?: string
  minReturningDepartureTime?: string
  region: string
  carriers?: Array<string>
  fareType?: string
  source?: string
  viewType?: string
  carrier?: string
  flightsCredit?: boolean
  forceBundleId?: string
  filterAirlines?: Array<string>
  dealId?: string
  allowIncompleteJourneys?: boolean
  charterSearchId?: string
  accommodationCheckInDate?: string
  excludeProviders?: Array<string>
  email?: string
  feederOriginAirportCode?: string
  /**
   * When true, svc-flights persists this flag on the Search Redis cache so the subsequent
   * /select call applies the feeder bundle override (FLY-5300). Set only on the feeder
   * /flightsearch call originating from the Maldives charter bundle checkout.
   */
  isFeederBundle?: boolean
}

interface FlightSearchResult {
  viewType?: string
  searchId?: string
  onewayFares?: Array<OneWayFare>
  fnfEligible?: boolean
  journeys?: Array<any>
  notices?: Array<App.FlightNotice>
  sort?: string
  fareFamilies?: FlightModels.Search.IFareFamilies
  metadata?: {
    cheapest?: {
      adultPrice?: number
      totalPrice?: number
      provider?: string
      departingFareId?: string
      returningFareId?: string
    }
  }
}

export interface ServerFlightDateChangeUpsellFaresResult {
  upsellPerLeg: Array<{ legId: string, upsellFares: Array<ServerUpsellFareFamily> }>
  isMixedUpsell?: boolean
}

interface ServerExchangeDateFlightSearchResult {
  onewayFares: Array<OneWayFare>
  fareFamilies: FlightModels.Search.IFareFamilies
  searchId: string
  fareIdsToUpsellFarePerLeg: Record<string, ServerFlightDateChangeUpsellFaresResult>
}

export interface MappedFlightSearchResult {
  onewayFares: Array<Array<App.JourneyV2>>
  searchId?: string
  sessionId?: string
  viewType?: string
  fnfEligible?: boolean
  metadata?: {
    cheapest?: {
      adultPrice?: number
      totalPrice?: number
      provider?: string
      departingFareId?: string
      returningFareId?: string
    }
  }
  timestamp?: number
  journeys?: Array<App.Journey>
  notices?: Array<App.FlightNotice>
  sort?: string
  combinedJourneys?: Array<App.FlightCombinedJourney>
}

export type CombinedJourney = App.FlightCombinedJourney

const fareTypeToTripTypeMapper: Record<FlightsFareTypes, string> = {
  [FlightsFareTypes.RETURN]: 'Return',
  [FlightsFareTypes.ONE_WAY]: 'OneWay',
  [FlightsFareTypes.MULTI_CITY]: 'MultiCity',
}

export const tripTypeToFareTypeMapper: Record<string, FlightsFareTypes> = {
  Return: FlightsFareTypes.RETURN,
  OneWay: FlightsFareTypes.ONE_WAY,
  MultiCity: FlightsFareTypes.MULTI_CITY,
  TwoOneWays: FlightsFareTypes.RETURN,
  TwoOneWaysAndReturn: FlightsFareTypes.RETURN,
}

function mapPairedCombinedJourneys(
  onewayFares: Array<Array<App.JourneyV2>>,
): Array<App.FlightCombinedJourney> {
  const departingJourneys = onewayFares[0] ?? []
  const returningJourneys = onewayFares[1] ?? []
  const returningJourneysByBookingKey = new Map(
    returningJourneys.map(returningJourney => [returningJourney.bookingInfo.boookingKey, returningJourney]),
  )

  return departingJourneys.flatMap((departingJourney) => {
    return nonNullable(departingJourney.pairedFareId.map((pairedFareId) => {
      const returningJourney = returningJourneysByBookingKey.get(pairedFareId)
      if (!returningJourney) {
        return undefined
      }

      return {
        departingJourney,
        returningJourney,
      }
    }))
  })
}

function mapCompatibleDomesticCombinedJourneys(
  departingJourneys: Array<App.JourneyV2>,
  returningJourneys: Array<App.JourneyV2>,
): Array<App.FlightCombinedJourney> {
  return DOMESTIC_COMBINED_JOURNEY_COMPATIBILITY_GROUPS.flatMap((contentGroup) => {
    const filteredDepartingJourneys = departingJourneys.filter((journey) => {
      return getTwoOneWayCompatibilityGroup(journey.bookingInfo.priceType) === contentGroup
    })
    const filteredReturningJourneys = returningJourneys.filter((journey) => {
      return getTwoOneWayCompatibilityGroup(journey.bookingInfo.priceType) === contentGroup
    })

    return filteredDepartingJourneys.flatMap((departingJourney) => {
      return filteredReturningJourneys.map((returningJourney) => {
        return {
          departingJourney,
          returningJourney,
        }
      })
    })
  })
}

function mapDomesticCombinedJourneys(
  onewayFares: Array<Array<App.JourneyV2>>,
): Array<App.FlightCombinedJourney> {
  const departingJourneys = onewayFares[0] ?? []
  const returningJourneys = onewayFares[1] ?? []
  const allCombinations = mapCompatibleDomesticCombinedJourneys(departingJourneys, returningJourneys)

  const sortedCombinations = sortDomesticCombinedFlightJourneys(allCombinations)
    .slice(0, MAX_DOMESTIC_COMBINED_JOURNEYS)
  const shortlistedCombinations = selectDomesticCombinedJourneyShortlist(sortedCombinations)
  const shortlistedCombinationsSet = new Set(shortlistedCombinations)
  const rankedShortlistedCombinations = shortlistedCombinations.map((combination, index) => ({
    ...combination,
    domesticBestSortRank: index + 1,
  }))
  const remainingCombinations = sortedCombinations.filter((combination) => {
    return !shortlistedCombinationsSet.has(combination)
  })

  return [
    ...rankedShortlistedCombinations,
    ...remainingCombinations,
  ]
}

export function mapCombinedJourneys(
  viewType: string | undefined,
  onewayFares: Array<Array<App.JourneyV2>>,
): Array<App.FlightCombinedJourney> {
  if (viewType === 'ReturnTwoOneWays') {
    return mapPairedCombinedJourneys(onewayFares)
  }

  if (viewType === 'TwoOneWays') {
    return mapDomesticCombinedJourneys(onewayFares)
  }

  return []
}

function getFlightSearchResultCount(onewayFares: Array<Array<App.JourneyV2>>): number {
  return onewayFares[0]?.length ?? 0
}

export function searchFlights(
  {
    itinerary,
    currency,
    occupancies,
    fareClass,
    maxArrivalTime,
    minReturningDepartureTime,
    region,
    fareType,
    source,
    viewType,
    carrier,
    flightsCredit,
    forceBundleId,
    filterAirlines,
    dealId,
    charterSearchId,
    allowIncompleteJourneys,
    accommodationCheckInDate,
    excludeProviders,
    email,
    feederOriginAirportCode,
    isFeederBundle,
  }: SearchParams,
  options: {
    offerId?: string
    publicOfferFlightPrice?: number
    offerCalendarPrice?: number
    enableSabreContent?: boolean
    requestLuxPlusPricing?: boolean
    applyDeclinedLuxPlusUpsell?: boolean
  } = EmptyObject,
): Promise<MappedFlightSearchResult> {
  const passengerCount = countOccupantsForFlights(occupancies)
  const { requestLuxPlusPricing, applyDeclinedLuxPlusUpsell, ...searchOptions } = options
  const isLuxPlus = getFlightSearchIsLuxPlusParam(!!requestLuxPlusPricing, !!applyDeclinedLuxPlusUpsell)

  const payload: any = {
    excludeProviders: excludeProviders,
    tripType: fareTypeToTripTypeMapper[fareType as FlightsFareTypes] || fareTypeToTripTypeMapper[FlightsFareTypes.RETURN],
    currencyCode: currency,
    itinerary,
    fareClass,
    passengers: {
      numAdults: passengerCount.adults,
      numChildren: passengerCount.children,
      numInfants: passengerCount.infants,
    },
    region,
    source,
    viewType,
    carrier,
    flightsCredit,
    sessionId: '',
    searchId: '',
    filters: {
      ...(filterAirlines ? { airlines: filterAirlines } : {}),
    },
    sortBy: '',
    searchDateTime: '',
    searchSource: '',
    localeId: '',
    apiAuthKey: '',
    forceBundleId,
    dealId,
    charterSearchId,
    allowIncompleteJourneys,
    ...searchOptions,
    ...(email ? { email } : {}),
    ...(isLuxPlus !== undefined ? { isLuxPlus } : {}),
    ...(feederOriginAirportCode ? { feederOriginAirportCode } : {}),
    ...(isFeederBundle ? { isFeederBundle: true } : {}),
  }

  const providerOverrides = config.FLIGHT_PROVIDER_OVERRIDE
  if (providerOverrides) {
    payload.provider = providerOverrides.split(',')
  }

  if (maxArrivalTime) {
    payload.maxArrivalTime = maxArrivalTime
  }

  if (minReturningDepartureTime) {
    payload.minReturningDepartureTime = minReturningDepartureTime
  }

  if (accommodationCheckInDate) {
    payload.accommodationCheckInDate = accommodationCheckInDate
  }

  const headers: Record<string, string> = { 'x-experiment-id': options?.enableSabreContent ? OptimizelyFeatureFlags.flightsEnableSabreContent : '' }
  const startedAt = performance.now()
  const trackingParams: Omit<TrackFlightSearchCompletedParams, 'durationMs' | 'status'> = {
    flightFlow: source ? 'bundled' : 'standalone',
    itinerary,
    occupancies,
    fareClass,
    fareType,
  }

  return request
    .post<App.ApiResponse<FlightSearchResult>, any>('/api/flights/flightsearch', payload, { credentials: 'include', headers })
    .then<MappedFlightSearchResult>(res => {
      const { onewayFares, searchId, viewType, sort, fareFamilies, metadata, fnfEligible } = res.result

      resetFlightsTimer()

      const mappedOneWayFares = (onewayFares ?? []).map((oneWayFare) => {
        return oneWayFare.fares.filter(fare => fare !== null).map((fare) => {
          return journeyV2Map(
            fare,
            fareFamilies,
            fnfEligible,
          )
        })
      })

      trackFlightSearchCompletedEvent({
        ...trackingParams,
        searchId,
        durationMs: Math.round(performance.now() - startedAt),
        status: 'success',
        resultCount: getFlightSearchResultCount(mappedOneWayFares),
      })

      return {
        onewayFares: mappedOneWayFares,
        searchId,
        viewType,
        fnfEligible,
        sort: sort?.toLowerCase(),
        fareFamilies,
        metadata,
        combinedJourneys: mapCombinedJourneys(viewType, mappedOneWayFares),
        timestamp: Date.now(),
      }
    })
    .catch((error) => {
      trackFlightSearchCompletedEvent({
        ...trackingParams,
        durationMs: Math.round(performance.now() - startedAt),
        status: 'error',
      })

      throw error
    })
}

export interface FareRuleParams {
  provider: string
  fare_rule_type?: string
  pnrId?: string
  region?: string
  journey_id?: string
  isBundledWithAccommodation?: boolean
}

export function getFareRules(url: string, fareRuleParams?: FareRuleParams) {
  if (fareRuleParams) {
    return request.post<App.ApiResponse<any>, unknown>(url, fareRuleParams).then(resp => resp.result)
  }
  return request.get<App.ApiResponse<any>>(url).then(resp => resp.result)
}

export type SelectedSeatPayload = {
  fareId: string
  flightNumber: string
  marketingAirlineCode: string
  operatingAirlineCode: string
  passengerId: string
  seatNumber: string
}

/**
 * Sabre checked-baggage ancillary selection sent to `POST /api/flights/select`.
 * Intentionally local: the contract defines this only INLINE inside the select operation
 * requestBody (`operations['/api/flights/select/post']…['ancillaries']`) with no named schema
 * export, so there is nothing clean to import. Shape verified identical to the contract inline
 * type (and svc-flights `src/api/flights/addons/types.ts`).
 */
export type AncillaryElectronicMiscellaneousDocumentType =
  | 'FLIGHT_COUPON_ASSOCIATED'
  | 'ETICKET'
  | 'STANDALONE'
  | 'STANDALONE_TICKET_ASSOCIATED'
  | 'OTHER_THAN_EMD'

export interface AncillarySelection {
  travellerId: string
  selectedFareId: string
  quantity: number
  ancillarySubCode: string
  airlineCode: string
  ancillaryGroupCode: 'BG'
  electronicMiscellaneousDocumentType: AncillaryElectronicMiscellaneousDocumentType
  basePriceAmount: number
}

export interface AncillariesForReservation {
  ancillaryOfferId: string
  totalCostAmount: number
  currency: string
  /**
   * Selections keyed by leg, i.e. by the `selectedFareId` of the fare that is that leg. Preferred:
   * checked baggage is priced per leg, so this is the offer's own scope.
   */
  ancillariesByLeg?: {
    [legId: string]: Array<AncillarySelection>
  }
  /**
   * @deprecated Selections keyed by a flight of the leg. Still accepted by `/select` (any flight of
   * the leg resolves that leg's single offer) and still sent when the availability response the
   * selection was priced against carried no leg key. Exactly one of the two is sent, never both.
   */
  ancillariesByFlight?: {
    [flightId: string]: Array<AncillarySelection>
  }
}

export type AdditionalSelectFlightsPayload = {
  seatSelections?: Array<SelectedSeatPayload>
  ancillaries?: AncillariesForReservation
}

export interface FlightSelectParams {
  journeyId: string
  departingJourneyKey: string
  departingFareFamilyId?: string
  returningJourneyKey?: string
  returningFareFamilyId?: string
  departingAirport: string
  arrivalAirport: string
  providerSearchId: string
  region?: string
  isStandalone?: boolean
  provider: string
  source?: string
  callCount?: number
  isFlightsCredit?: boolean
  viewType?: string
  selectedFareIds?: Array<string>
  isFlightBaggageAvailable?: boolean
  forceBundleId?: string
  selectedUpsellOptions?: Record<string, string>
  additionalPayload?: AdditionalSelectFlightsPayload
  isFnFBooking: boolean
  /**
   * Optional feeder origin airport code (e.g. 'SYD'). Forwarded to svc-flights /select so
   * charter feeder markup is applied dynamically: absent (customer skipped the connecting
   * flight) → MEL self-feeder markup applied to charter fares; present → markup not applied.
   * Source the value via `getFeederOriginAirportCode(state)`.
   */
  feederOriginAirportCode?: string
}

export interface PriceDetails {
  passengerTypeQuantity: { code: string }
  totalFare: string
  totalFareOriginalCurrency: number
}

export interface Price {
  type: 'total_fare' | 'atol_fee' | 'service_fee' | 'flights_credit_fee' | 'booking_fee'
  amount: number
}

type SelectFlightsResponse = paths['/api/flights/select']['post']['responses'][200]['content']['application/json']
type SelectFlightsResult = Extract<SelectFlightsResponse, { status: 200 }>['result'] & {
  selectedSeatsInformation?: { totalCost: number }
}

export function selectflights({
  journeyId,
  departingJourneyKey,
  departingFareFamilyId,
  returningJourneyKey,
  returningFareFamilyId,
  departingAirport,
  arrivalAirport,
  providerSearchId,
  region,
  isStandalone,
  provider,
  source,
  isFlightsCredit,
  viewType,
  selectedFareIds,
  forceBundleId,
  selectedUpsellOptions,
  additionalPayload,
  isFnFBooking,
  feederOriginAirportCode,
}: FlightSelectParams): Promise<App.ApiResponse<SelectFlightsResult>> {
  const selectFlightDetails: any = {
    journeyId,
    departing_journey_key: departingJourneyKey,
    returning_journey_key: returningJourneyKey,
    departing_airport: departingAirport,
    arrival_airport: arrivalAirport,
    provider_search_id: providerSearchId,
    region,
    isStandalone,
    provider,
    source,
    isFlightsCredit,
    viewType,
    selectedFareIds,
    forceBundleId,
    isFnFBooking,
    ...(feederOriginAirportCode ? { feederOriginAirportCode } : {}),
  }

  if (selectedUpsellOptions && Object.keys(selectedUpsellOptions).length > 0) {
    selectFlightDetails.selectedUpsellOptions = selectedUpsellOptions
  } else if (selectedFareIds && (departingFareFamilyId || returningFareFamilyId)) {
    // Use default mapping if selectedUpsellOptions is not provided
    selectFlightDetails.selectedUpsellOptions = {
      [selectedFareIds[0]]: departingFareFamilyId,
      [selectedFareIds[1]]: returningFareFamilyId,
    }
  }

  if (additionalPayload?.seatSelections) {
    selectFlightDetails.selectedSeats = additionalPayload.seatSelections
  }

  if (additionalPayload?.ancillaries) {
    selectFlightDetails.ancillaries = additionalPayload.ancillaries
  }

  // disable booking fee for any flights in AU region, apply merchant fee for merchant fee evaluation
  return request
    .post<App.ApiResponse<SelectFlightsResult>, any>('/api/flights/select', selectFlightDetails).then(response => {
      if (response.result.error?.message) {
        throw new Error(response.result.error.message)
      }
      return response
    })
}

type PopularAirportsResponse = paths['/api/flights/popularDestinations']['get']['responses']['200']['content']['application/json']
type ServerAirportLocation = PopularAirportsResponse['result']['origins'][number]

function airportMap(airport: ServerAirportLocation): App.AirportLocation {
  return {
    id: airport.id,
    cityAirportName: airport.cityAirportName,
    cityCode: airport.cityCode,
    cityDesc: airport.cityDesc,
    airportCode: airport.airportCode,
    airportName: airport.airportName,
    country: airport.country,
    isMultiAirport: airport.isMultiAirport,
    isChildDisplay: airport.isChildDisplay,
  }
}

interface AirportResult {
  airports?: Record<string, App.Airport>
  origins?: Array<string>
  destinations?: Array<string>
  main_port?: string
  closest_port?: string
}

export function getAirports(regionCode: string): Promise<{
  airports: Array<App.Airport>
  originAirports: Array<App.Airport>
  mainAirport?: App.Airport
}> {
  const query = qs.stringify({ region: regionCode })

  return request.get<App.ApiResponse<AirportResult>>(`/api/flights/airports?${query}`).then(res => {
    const originAirports = nonNullable((res.result.origins ?? []).map(code => res.result.airports?.[code]))
    return {
      airports: Object.values(res.result.airports ?? {}),
      originAirports: sortBy(originAirports, airport => airport.name, 'asc'),
      mainAirport: res.result.airports?.[res.result.main_port ?? ''],
    }
  })
}

export async function getFlightPrice({
  startDate,
  endDate,
  exactSabrePrice = false,
  numberOfAdults,
  numberOfChildren,
  numberOfInfants,
  origin,
  destination,
  currency,
  region,
  nights,
}: {
  startDate: string
  endDate: string
  exactSabrePrice?: boolean
  numberOfAdults: number
  numberOfChildren: number
  numberOfInfants: number
  origin?: string
  destination: string
  currency: string
  region: string
  nights?: number
}): Promise<number> {
  const uri = templates.flight.flight_single_cheapest.expand({
    start_date: startDate,
    // single-cheapest treats end_date as the last possible departure date. For
    // a selected stay, number_of_nights determines the exact return date.
    end_date: exactSabrePrice ? startDate : endDate,
    origin,
    currency,
    number_of_nights: nights,
    number_of_adults: numberOfAdults,
    number_of_children: numberOfChildren,
    number_of_infants: numberOfInfants,
    region,
    destination,
    provider: exactSabrePrice ? ['sabre'] : undefined,
  })

  const response = await request.get<App.ApiResponse<any>>(uri)

  if (response?.result?.journey) {
    return response.result.journey.cost + response.result.journey.fees
  }
  return 0
}

export function getPopularAirportsByRegion(region: string) {
  return request
    .get<PopularAirportsResponse>(`/api/flights/popularDestinations?region=${region}`)
    .then(res => {
      const { origins, destinations } = res.result

      return {
        origins: origins.map(airport => airportMap(airport)),
        destinations: destinations.map(airport => airportMap(airport)),
      }
    })
}

export function getCreditRebookingDetails(email: string, pnrId: string) {
  return request
    .post<App.ApiResponse<any>, Record<string, string>>('/api/flights/postBookingPnrDetails', { email, pnrId }, { credentials: 'include' })
    .then(res => {
      const { status, message, result } = res

      const creditDetails: FlightsCreditDetails = {
        passengerCounts: result.passengerCounts,
        flightCreditFee: result.flightCreditFee,
        currency: result.currency,
        carrier: result.carrier?.validating,
        carrierName: result.carrier?.carrierName,
        pnrId,
        region: result.region,
        email: result.email,
        phone: result.phone,
      }

      return {
        status,
        message,
        creditDetails,
      }
    })
}

export function getFlightDealsByRegion(region: string, dealCategory?: string): Promise<Array<App.FlightDeal>> {
  return request
    .post<App.ApiResponse<Array<App.FlightDeal>>, Record<string, string | undefined>>('/api/flights/flight-deals/getFilteredDeals', { region, categoryGroup: dealCategory })
    .then(res => res.result.map(flightDeal => flightDealMap(flightDeal)))
}

export function getFlightDeal(id: string, token?: string): Promise<App.FlightDeal> {
  return request.get<App.ApiResponse<App.FlightDeal>>(`/api/flights/flight-deals/${id}`, {
    credentials: 'include',
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  }).then(response => flightDealMap(response.result))
}

interface GetFlightCampaignsResult {
  rows: Array<App.FlightCampaign>
  count: number
  isEnabled: boolean
}

export function getFlightCampaigns(region: string): Promise<GetFlightCampaignsResult> {
  return request.get<App.ApiResponse<GetFlightCampaignsResult>>(`/api/flights/campaigns?region=${region}&active=true`)
    .then(res => res.result)
}

export function getFlightCampaign(id: string): Promise<App.FlightCampaign> {
  return request.get<App.ApiResponse<App.FlightCampaign>>(`/api/flights/campaigns/${id}`).then(res => res.result)
}

export function getFlightCampaignDeals(id: string): Promise<Array<App.FlightDeal>> {
  return request.get<App.ApiResponse<{ rows: Array<App.FlightDeal>, count: number }>>(`/api/flights/campaigns/${id}/deals`).then(res => res.result.rows.map(flightDealMap))
}

export function getAirlines(searchText: string): Promise<Array<App.Airline>> {
  return request
    .get<Array<Airline>>(`/api/flights/airlines?searchCode=${searchText}`)
    .then(res => res.map(airline => airline))
}

export async function getJourneyV2(
  searchId: string,
  fareId: string,
  accessToken: string,
  fareFamilyId?: string,
  feederOriginAirportCode?: string,
): Promise<App.JourneyV2> {
  const query = qs.stringify({
    selectedUpsellOptionId: fareFamilyId,
    ...(feederOriginAirportCode ? { feederOriginAirportCode } : {}),
  })
  return request.get<App.ApiResponse<Fare>>(
    `/api/flights/flightSearch/${searchId}/${fareId}?${query}`,
    authOptions(accessToken),
  ).then(response => {
    // server doesn't respond with correct status but hides the status in a 200 response, we need to detect this
    if (response.errors?.message) {
      throw new Error(response.errors.message)
    }
    return journeyV2Map(response.result)
  })
}

export async function getDateChangeJourneyV2(
  reservationId: string,
  searchId: string,
  fareId: string,
  selectedUpsellOptionId?: string,
): Promise<App.JourneyV2> {
  const queryParams = selectedUpsellOptionId ? `?selectedUpsellOptionId=${selectedUpsellOptionId}` : ''
  return request.get<App.ApiResponse<Fare>>(`/api/flights/reservation/${reservationId}/addons/exchange-dates/search/${searchId}/${fareId}${queryParams}`, { credentials: 'include' })
    .then(response => {
      if (response.errors?.message) {
        throw new Error(response.errors.message)
      }
      return journeyV2Map(response.result)
    })
}

interface CachePricingRequest {
  startDate: string
  endDate: string
  origin: string
  destination: string
  currency: string
  region: string
  numberOfNights: number
  tripType?: App.FlightCalendarTripType
  fareClass?: App.FlightsFareClass
}

interface CachePricingResult {
  journeys_by_date: FlightCachedPrice
  earliest_booking_date: string
  final_booking_date: string
}

export function getFlightCachePrices(params: CachePricingRequest): Promise<FlightCalendarPrices> {
  const { startDate, endDate, origin, destination, currency, region, numberOfNights, tripType, fareClass } = params
  const queryParams = {
    start_date: startDate,
    end_date: endDate,
    number_of_adults: 2,
    number_of_children: 0,
    number_of_infants: 0,
    number_of_nights: numberOfNights,
    destination,
    origin,
    currency,
    region,
    trip_type: tripType,
    fare_class: fareClass,
  }

  return request
    .get<App.ApiResponse<CachePricingResult>>(`/api/flights/calendar-search?${qs.stringify(queryParams)}`)
    .then(res => ({ cachedPrices: res.result.journeys_by_date, earliestBookingDate: res.result.earliest_booking_date, finalBookingDate: res.result.final_booking_date }))
}

interface GetFlightUpsellFaresParams {
  searchId: string
  sessionId: string
  origin: string
  destination: string
  occupancies: App.Occupants
  region: string
  departingUpsellOptionId?: string
  isStandalone?: boolean
  selectedFareIds: Array<string>
  feederOriginAirportCode?: string
  email?: string
  requestLuxPlusPricing?: boolean
  applyDeclinedLuxPlusUpsell?: boolean
}

export interface UpsellFaresResult {
  upsellFares: Array<ServerUpsellFareFamily>
  upsellPerLeg?: Array<{ legId: string, upsellFares: Array<ServerUpsellFareFamily> }>
  isMixedUpsell?: boolean
}

export type FareFamiliesResponse = {
  upsellFares: Array<App.FlightFareFamily>
  upsellFaresPerLeg?: Array<Array<App.FlightFareFamily>>
  isMixedUpsell?: boolean
}

export function getFlightFareFamilies({
  sessionId,
  searchId,
  origin,
  destination,
  occupancies,
  region,
  departingUpsellOptionId,
  isStandalone,
  selectedFareIds,
  feederOriginAirportCode,
  email,
  requestLuxPlusPricing,
  applyDeclinedLuxPlusUpsell,
}: GetFlightUpsellFaresParams): Promise<FareFamiliesResponse> {
  const isLuxPlus = getFlightSearchIsLuxPlusParam(!!requestLuxPlusPricing, !!applyDeclinedLuxPlusUpsell)
  const queryParams = {
    origin,
    destination,
    numAdults: occupancies.adults,
    numChildren: occupancies.children,
    numInfants: occupancies.infants,
    region,
    sessionId,
    searchId,
    departingUpsellOptionId,
    isStandalone,
    selectedFareIds: selectedFareIds?.join(','),
    ...(feederOriginAirportCode ? { feederOriginAirportCode } : {}),
    email,
    ...(isLuxPlus !== undefined ? { isLuxPlus } : {}),
  }

  return request
    .get<App.ApiResponse<UpsellFaresResult>>(`/api/flights/fare-families/upsell?${qs.stringify(queryParams)}`)
    .then(res => {
      const upsellFares = res.result.upsellFares.map(mapFlightFareFamily)
      const upsellFaresPerLeg = res.result.upsellPerLeg?.map(leg => leg.upsellFares.map(mapFlightFareFamily))

      return { upsellFares, upsellFaresPerLeg, isMixedUpsell: res.result.isMixedUpsell }
    })
}

interface UpdateFlightTravellers {
  passengers: Array<App.FlightTraveller>
}

export function updateFlightReservationTravellers(reservationId: string, passengers: Array<App.FlightTraveller>) {
  const url = `/api/flights/reservation/${reservationId}/travellers`
  return request.put<App.ApiResponse<UpdateFlightTravellers>, UpdateFlightTravellers>(url, { passengers }, { credentials: 'include' })
    .then((res) => res.result.passengers)
}

interface UpdateFlightReservation {
  contactInfo: {
    email: string
    phone: string
  }
  emergencyContact?: App.FlightEmergencyContact
}

interface UpdateFlightReservationDetails {
  contact_info: {
    email: string
    phone: string
  }
  emergency_contact?: {
    country_code: string
    first_name: string
    last_name: string
    phone: string
    relationship: string
  }
}

export function updateFlightReservationDetails(reservationId: string, { contactInfo, emergencyContact }: UpdateFlightReservation) {
  const url = `/api/flights/reservation/${reservationId}/details`
  return request.put<App.ApiResponse<App.FlightOrderItemDetails>, UpdateFlightReservationDetails>(url, {
    contact_info: contactInfo,
    emergency_contact: emergencyContact ? {
      country_code: emergencyContact.countryCode,
      first_name: emergencyContact.firstName,
      last_name: emergencyContact.lastName,
      phone: emergencyContact.phone,
      relationship: emergencyContact.relationship,
    } : undefined,
  }, {
    credentials: 'include',
  })
    .then(res => {
      return res.result
    })
}

export function getRecentFlightSearches(): Promise<Array<App.RecentFlightSearch>> {
  const url = '/api/flights/flightSearch/recent'

  return request.get<App.ApiResponse<Array<App.RecentFlightSearch>>>(url, {
    credentials: 'include',
  }).then(res => res.result)
}

export function lockFlightReservation(reservationId: string) {
  return request.patch<App.ApiResponse, { op: string }>(
    `/api/flights/reservation/${reservationId}`,
    { op: 'lock' },
    { credentials: 'include' },
  ).then((res) => {
    return res.result
  })
}

export interface MappedDateChangeFlightSearchResult extends MappedFlightSearchResult {
  fareIdsToUpsellFarePerLeg: Record<string, App.FlightDateChangeUpsellFaresResult>
}

export function getFlightsDateChangeFlightsList(reservationId: string, region: string, selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>): Promise<MappedDateChangeFlightSearchResult> {
  const url = `/api/flights/reservation/${reservationId}/addons/exchange-dates/search`

  return request.post<App.ApiResponse<ServerExchangeDateFlightSearchResult>, any>(url, {
    region,
    selectedLegs: selectedLegs.map(leg => ({
      legId: leg.id,
      // TODO: update once calendar is implemented
      newDepartureDate: leg.newDepartureDate,
    })),
  }, { credentials: 'include' })
    .then<MappedDateChangeFlightSearchResult>(res => {
      const { onewayFares, fareFamilies, searchId } = res.result

      return {
        onewayFares: (onewayFares ?? []).map((oneWayFare) => {
          return oneWayFare.fares.filter(fare => fare !== null).map((fare) => journeyV2Map(fare, fareFamilies))
        }),
        fareFamilies,
        searchId,
        timestamp: Date.now(),
        fareIdsToUpsellFarePerLeg: Object.keys(res.result.fareIdsToUpsellFarePerLeg).reduce((acc, legId) => {
          const upsellFares = res.result.fareIdsToUpsellFarePerLeg[legId]
          const upsellPerLeg = (upsellFares.upsellPerLeg ?? []).map(leg => leg.upsellFares.map(mapFlightFareFamily))

          acc[legId] = { upsellPerLeg, isMixedUpsell: upsellFares.isMixedUpsell }
          return acc
        }, {} as Record<string, App.FlightDateChangeUpsellFaresResult>),
      }
    })
}

export interface FlightSeatTraveler {
  id: string;
  type: 'ADT' | 'CHD' | 'INF';
  givenName?: string;
  surname?: string;
  loyaltyProgramNumbers?: {
    frequentFlyer: Array<{
      bookingCarrier: string;
      programCarrier: string;
      number: string;
    }>;
  };
}

interface GetSeatsParams {
  searchId: string;
  currency: string;
  region: string;
  isStandalone: boolean;
  brand: string;
  selectedFareIds: Array<string>;
  travelers: Array<FlightSeatTraveler>;
}

export function getFlightSeats(params: GetSeatsParams): Promise<App.FlightsSeatsAvailability> {
  const { searchId, currency, region, isStandalone, brand, selectedFareIds, travelers } = params

  return request.post<App.ApiResponse<ServerSeatResponse>, GetSeatsParams>('/api/flights/addons/seats/availability', {
    searchId,
    currency,
    region,
    isStandalone,
    brand,
    selectedFareIds,
    travelers,
  }, { credentials: 'include' })
    .then(res => seatsAvailabilityMap(res.result))
}

/**
 * Seat map for a booking that already exists, rather than for a search result.
 *
 * Takes a reservation id instead of a searchId and traveller list, because everything it needs is
 * already on the booking. The response is the same shape as the pre-booking map so it renders
 * through the same components. A cheaper change is still offered; checkout charges it at $0
 * because there is no refund of what was already paid.
 */
export function getPostBookingFlightSeats(reservationId: string): Promise<App.FlightsSeatsAvailability> {
  return request.get<App.ApiResponse<ServerSeatResponse>>(
    `/api/flights/addons/seats/availability/${reservationId}`,
    { credentials: 'include' },
  )
    .then(res => seatsAvailabilityMap(res.result))
}

/**
 * Purchasable baggage for a booking that already exists.
 *
 * Takes a reservation id rather than a searchId and traveller list: everything needed is on the
 * booking. Calling this also primes a 30 minute Redis cache in svc-flights, keyed by reservation —
 * and the addon create reads that cache rather than re-fetching from the provider, so the purchase
 * has to complete inside that window. `BAGGAGE_OFFER_WINDOW_MS` is what enforces it on our side.
 */
export function getPostBookingFlightBaggage(reservationId: string): Promise<PostBookingBaggageAvailability> {
  return request.get<App.ApiResponse<ServerReservationBaggagesResult>>(
    `/api/flights/reservation/${reservationId}/addons/baggages`,
    { credentials: 'include' },
  )
    .then(res => postBookingBaggageMap(res.result))
}

// Baggage availability types are sourced directly from the svc-flights contract.
export type ServerBaggageAvailabilityResponse = flightDefinitions['BaggageAvailabilityResponse']
export type ServerBaggageAvailabilityDefinition = flightDefinitions['BaggageAvailability.Definition']
export type ServerBaggageAvailabilityOffer = flightDefinitions['BaggageAvailability.Offer']

// Post-booking (My Escapes) baggage add-on types, also sourced directly from the contract.
export type ServerReservationBaggagesResult = flightDefinitions['ReservationBaggages.Result']
export type ServerReservationBaggagesOption = flightDefinitions['ReservationBaggages.Option']

// Request traveler. NOTE: baggage uses 'CNN' for a child (unlike `FlightSeatTraveler` → 'CHD')
// and requires firstName/lastName — see the contract `BaggageAvailability.Traveler`.
export type BaggageAvailabilityTraveler = flightDefinitions['BaggageAvailability.Traveler']

interface GetBaggageAvailabilityParams {
  searchId: string;
  currency: string;
  region: string;
  isStandalone?: boolean;
  brand: string;
  selectedFareIds: Array<string>;
  travelers: Array<BaggageAvailabilityTraveler>;
}

// Mirrors `getFlightSeats`: sends `isStandalone` (flights-only checkout) so svc-flights forwards
// it to Sabre. The contract request is { searchId, travelers, selectedFareIds, region, currency,
// brand, isStandalone? }.
export function getBaggageAvailability(params: GetBaggageAvailabilityParams): Promise<App.FlightsBaggageAvailability> {
  const { searchId, currency, region, isStandalone, brand, selectedFareIds, travelers } = params

  return request.post<App.ApiResponse<ServerBaggageAvailabilityResponse>, GetBaggageAvailabilityParams>('/api/flights/addons/baggage/availability', {
    searchId,
    currency,
    region,
    isStandalone,
    brand,
    selectedFareIds,
    travelers,
  }, { credentials: 'include' })
    .then(res => baggageAvailabilityMap(res.result))
}

export function getFlightPenaltiesDetails(reservationId: string, legId?: string): Promise<App.FlightPenaltiesDetails> {
  const queryParams = new URLSearchParams()
  if (legId) {
    queryParams.set('legId', legId)
  }
  const queryString = queryParams.toString()
  let url = `/api/flights/reservation/${reservationId}/penalties`
  if (queryString) {
    url = `${url}?${queryString}`
  }

  return request.get<App.ApiResponse<App.FlightPenaltiesDetails>>(url, {
    credentials: 'include',
  })
    .then(res => res.result)
}

export interface FlightDateGridRequest {
  departureDate: string
  returnDate?: string
  alternativeDatesCount: number
  origin: string
  destination: string
  passengers: {
    adults: number
    children?: number
    infants?: number
  }
  region: string
  brand?: string
  carrier?: string
  fareClass?: string
  isLuxPlus?: boolean
}

export function getFlightDateGrid(params: FlightDateGridRequest): Promise<App.FlightDateGridResponse> {
  return request.post<App.ApiResponse<App.FlightDateGridResponse>, FlightDateGridRequest>(
    '/api/flights/calendar-grid', params,
    { credentials: 'include' },
  ).then(res => res.result)
}

export function createFareWatch(
  userId: string,
  payload: CreateFareWatchRequest,
): Promise<FareWatch> {
  return request.post<CreateFareWatchResponse, CreateFareWatchRequest>(
    `/api/customer-communication/v1/users/fare-watches?userId=${encodeURIComponent(userId)}&brand=${config.BRAND}`,
    payload,
    { credentials: 'include', excludeBrand: true },
  ).then(res => res.result.fareWatch)
}

export function fetchFareWatches(userId: string): Promise<Array<FareWatch>> {
  return request.get<GetFareWatchesResponse>(
    `/api/customer-communication/v1/users/fare-watches?userId=${encodeURIComponent(userId)}&brand=${config.BRAND}`,
    { credentials: 'include', excludeBrand: true },
  ).then(res => res.result.fareWatches)
}

/**
 * Replaces an existing fare watch. The service takes the whole watch rather than a patch, so
 * callers send every field even when only one changed.
 *
 * Errors are left to reject: unlike a read, a failed write here is never a normal state — a 404
 * means the alert was removed elsewhere and a 409 that another alert already covers the new route,
 * and the user has to be told either way rather than shown a success.
 */
export function updateFareWatch(
  fareWatchId: string,
  userId: string,
  payload: UpdateFareWatchRequest,
): Promise<FareWatch> {
  return request.put<UpdateFareWatchResponse, UpdateFareWatchRequest>(
    `/api/customer-communication/v1/users/fare-watches/${encodeURIComponent(fareWatchId)}?userId=${encodeURIComponent(userId)}&brand=${config.BRAND}`,
    payload,
    { credentials: 'include', excludeBrand: true },
  ).then(res => res.result.fareWatch)
}

export function deleteFareWatch(fareWatchId: string, userId: string): Promise<void> {
  return request.delete(
    `/api/customer-communication/v1/users/fare-watches/${encodeURIComponent(fareWatchId)}?userId=${encodeURIComponent(userId)}&brand=${config.BRAND}`,
    { credentials: 'include', excludeBrand: true },
  )
}

export function fetchFareWatchStatus(userId: string, query: FareWatchStatusQuery): Promise<FareWatchStatusResponse['result']> {
  const params = new URLSearchParams({
    userId,
    brand: config.BRAND,
    tripType: query.tripType,
    fareClass: query.fareClass,
    itineraries: JSON.stringify(query.itineraries),
  })
  return request.get<FareWatchStatusResponse>(
    `/api/customer-communication/v1/users/fare-watches/status?${params}`,
    { credentials: 'include', excludeBrand: true },
  ).then(res => res.result)
}

export interface CabinUpgradeSearchResult {
  searchId: string
  upgradeOptions: {
    currentCabinClass: string
    outboundDepDate: string | null
    returnDepDate: string | null
    availableUpgrades: Array<App.UpgradeOption>
  }
}

export function searchCabinUpgradeOptions(
  reservationId: string,
): Promise<CabinUpgradeSearchResult> {
  const url = `/api/flights/reservation/${reservationId}/addons/cabin-upgrade/search`

  return request.post<App.ApiResponse<CabinUpgradeSearchResult>, Record<string, never>>(
    url,
    {},
    { credentials: 'include' },
  ).then(res => res.result)
}

interface ServerFareSenseScoredFare extends Fare {
  score: number
}

interface ServerFareSenseResult extends Omit<App.FareSenseResponse, 'suggestedFares'> {
  suggestedFares: Array<ServerFareSenseScoredFare | null>
}

export function getFareSenseSuggestions(searchId: string, offerId?: string): Promise<App.FareSenseResponse> {
  const query = qs.stringify({
    offerId,
  })

  return request.get<App.ApiResponse<ServerFareSenseResult>>(
    `/api/flights/faresense/suggestions/${encodeURIComponent(searchId)}${query ? `?${query}` : ''}`,
    { credentials: 'include' },
  ).then(res => ({
    ...res.result,
    suggestedFares: res.result.suggestedFares.map(fare => {
      if (!fare) return null

      return {
        ...journeyV2Map(fare),
        score: fare.score,
      }
    }),
  }))
}

export interface CharterFlightPromotionAvailableSeats {
  seatsLeft: number
}

export function getCharterPromotionAvailableSeats(
  promotionType: CharterPromotionType,
): Promise<CharterFlightPromotionAvailableSeats> {
  return request.get<App.ApiResponse<CharterFlightPromotionAvailableSeats>>(
    `/api/flights-charter/v1/fare-families/promotions/${promotionType}/seats-left`,
    { credentials: 'include' },
  ).then(res => res.result)
}

export function validateFrequentFlyerNumbers(params: {
  brand: string
  region: string
  travelers: Array<App.FrequentFlyerValidationTraveler>
}): Promise<Array<App.FrequentFlyerValidationResult>> {
  return request.post<App.ApiResponse<{ results: Array<App.FrequentFlyerValidationResult> }>, typeof params>(
    '/api/flights/frequent-flyer/validate', params, { credentials: 'include' },
  ).then(response => response.result.results)
}
